// Per-tool config for the generic ToolFormPrompt.vue widget. Each entry describes
// a small structured form for a single read-only lookup tool in agents/tools/*.js:
// what fields to collect, and how to turn the filled-in values into one plain-text
// answer that gets sent back into the chat (same contract as buildBoostAnswer.js —
// the model still calls the real tool, this only replaces free-text parsing with
// structured input).
//
// Keep field sets small and focused on what actually matters for accuracy; these
// are not meant to expose every parameter a tool supports (see search_field_lens_table
// below), just enough to remove ambiguity from the common cases.

// create_task's answer builder is bespoke (not part of TOOL_PROMPTS) because /task
// reuses the app's existing ModalTask.vue dialog instead of the generic
// ToolFormPrompt — see AssistantChat.vue. `task` is the object ModalTask.vue emits
// via `submitTask`, the same shape Task/Dashboard.vue and Task/Container.vue POST
// to /tasks directly; here it's translated into an instruction for the model to
// call create_task instead, since the modal submit already is the user's
// confirmation (create_task's userValidated gate).
//
// Note: create_task always assigns to the current user server-side (context.user_id)
// — it has no assignee parameter — so a different assignee picked in the modal
// can't be honored through this tool and is intentionally left out of the answer.
function extractRelatedToIds(relatedTo) {
  if (!Array.isArray(relatedTo)) return []

  return relatedTo
    .map((entry) => (entry && typeof entry === 'object' ? entry.value : entry))
    .filter(Boolean)
}

export function buildTaskAnswer(task) {
  const relatedToIds = extractRelatedToIds(task.details?.relatedTo)
  const dueDate = task.dueDate ? new Date(task.dueDate).toLocaleDateString('en-US') : null

  const lines = [
    'Create a new Task. The user already filled out and submitted the task form, so treat every value below as confirmed — call create_task with userValidated set to true using these exact values:',
    `- Title: ${task.title}`,
  ]

  if (dueDate) lines.push(`- Due date: ${dueDate}`)
  if (task.details?.priority) lines.push(`- Priority: ${task.details.priority}`)
  if (task.details?.status) lines.push(`- Status: ${task.details.status}`)
  if (task.type) lines.push(`- Category: ${task.type}`)
  if (task.details?.comment) lines.push(`- Description: ${task.details.comment}`)
  if (task.details?.contactId) lines.push(`- Contact (Salesforce Contact ID): ${task.details.contactId}`)
  if (relatedToIds.length) lines.push(`- Related to (Salesforce IDs): ${relatedToIds.join(', ')}`)
  if (task.details?.duration) lines.push(`- Duration: ${task.details.duration} hours`)
  if (task.products) lines.push(`- Products: ${task.products}`)
  if (task.reminderInterval) {
    const customPart = task.reminderInterval === 'Custom' && task.customReminderDate
      ? ` (${new Date(task.customReminderDate).toLocaleString()})`
      : ''
    lines.push(`- Reminder: ${task.reminderInterval}${customPart}`)
  }

  return {
    display: `Create task: "${task.title}"`,
    text: lines.join('\n'),
  }
}

// create_fieldlens_request's answer builder, mirroring buildTaskAnswer above.
// `payload` is what FieldLensRequestWizard.vue emits via `submit` — see that
// component for field definitions. Uses `rawDescription` (not the ISV-contact-
// folded `description`) because create_fieldlens_request folds the isv_contact_*
// fields into the description itself; passing the already-folded text would
// duplicate that block.
export function buildFieldLensRequestAnswer(payload) {
  const accountLabel = payload.account?.name ? `${payload.account.name} (${payload.account.id})` : payload.account?.id
  const displayTitle = payload.isISVType
    ? [payload.productName, payload.productVersion].filter(Boolean).join(' ')
    : payload.title

  const lines = [
    'Create a new Field Lens request. The user already filled out and submitted the request wizard, so treat every value below as confirmed — call create_fieldlens_request with these exact values:',
    `- Account (Salesforce Account ID): ${accountLabel}`,
    `- Type ID: ${payload.typeId}${payload.typeName ? ` (${payload.typeName})` : ''}`,
    `- Subtype ID: ${payload.subtypeId}${payload.subtypeName ? ` (${payload.subtypeName})` : ''}`,
  ]

  if (payload.isISVType) {
    lines.push(`- Product name: ${payload.productName}`)
    lines.push(`- Partner name: ${payload.partnerName}`)
    lines.push(`- Product version: ${payload.productVersion}`)
  }
  else {
    lines.push(`- Product name: ${payload.relatedEntity}`)
    lines.push(`- Title: ${payload.title}`)
  }

  if (payload.rawDescription) lines.push(`- Description: ${payload.rawDescription}`)
  if (payload.jiraURL) lines.push(`- Jira URL: ${payload.jiraURL}`)
  if (payload.isvContactName) lines.push(`- ISV contact name: ${payload.isvContactName}`)
  if (payload.isvContactEmail) lines.push(`- ISV contact email: ${payload.isvContactEmail}`)
  if (payload.isvContactPhone) lines.push(`- ISV contact phone: ${payload.isvContactPhone}`)
  if (payload.isvContactRole) lines.push(`- ISV contact role: ${payload.isvContactRole}`)
  if (payload.industrySegment) lines.push(`- Industry segment: ${payload.industrySegment}`)
  if (payload.opportunity?.id) lines.push(`- Related Opportunity (Salesforce ID): ${payload.opportunity.id}`)
  if (payload.supportCase?.id) lines.push(`- Related SE Support Case (Salesforce ID): ${payload.supportCase.id}`)

  return {
    display: `Create Field Lens request: "${displayTitle}"`,
    text: lines.join('\n'),
  }
}

// Only for slash commands whose lookup needs a fixed set of choices (a `select`
// field, e.g. /winloss's status). A blank that's a Salesforce record (an
// `account` field) doesn't need a form of its own — the chat input's built-in
// @mention search already does that lookup inline (see /health's `template` in
// useMentionAutocomplete.js) — and anything else is plain free text, which drops
// a fill-in-the-blank draft into the input instead (same shorthand as the
// empty-state suggested-prompt chips). Neither has anything to key into this map.
export const TOOL_PROMPTS = {
  winloss: {
    key: 'winloss',
    title: 'Win/loss reasons',
    description: 'Get win/loss reasons and competitive intel against a competitor.',
    fields: [
      { name: 'competitor', label: 'Competitor', type: 'text', required: true, placeholder: 'e.g. vSAN, HPE, Pure Storage' },
      { name: 'status', label: 'Status', type: 'select', options: ['Won', 'Lost', 'Both'], default: 'Both' },
    ],
    isValid: (values) => Boolean(values.competitor?.trim()),
    buildAnswer(values) {
      const status = values.status || 'Both'
      return `Get win/loss reasons and competitive intel against competitor "${values.competitor.trim()}" (status: ${status}). Call get_win_loss_reasons with these criteria.`
    },
  },

  compat: {
    key: 'compat',
    title: 'Compatibility matrix',
    description: 'Check if a 3rd-party product is supported and validated on Nutanix.',
    fields: [
      { name: 'partner_name', label: 'Vendor / Partner', type: 'text', required: true, placeholder: 'e.g. Commvault, Cisco' },
      { name: 'product_name', label: 'Product', type: 'text', placeholder: 'e.g. Complete Backup & Recovery' },
      { name: 'hypervisor', label: 'Hypervisor', type: 'select', options: ['AHV', 'ESXi'] },
      { name: 'nutanix_aos_version', label: 'AOS Version', type: 'text', placeholder: 'e.g. 6.5' },
      { name: 'nutanix_component', label: 'Component', type: 'text', placeholder: 'e.g. Files, Objects, NC2' },
    ],
    isValid: (values) => Boolean(values.partner_name?.trim()),
    buildAnswer(values) {
      const details = []
      if (values.product_name?.trim()) details.push(`product "${values.product_name.trim()}"`)
      if (values.hypervisor) details.push(`hypervisor ${values.hypervisor}`)
      if (values.nutanix_aos_version?.trim()) details.push(`AOS version ${values.nutanix_aos_version.trim()}`)
      if (values.nutanix_component?.trim()) details.push(`component ${values.nutanix_component.trim()}`)
      const detailText = details.length ? ` (${details.join(', ')})` : ''
      return `Check the Nutanix compatibility matrix for vendor "${values.partner_name.trim()}"${detailText}. Call check_compatibility_matrix with these criteria.`
    },
  },

  fieldlens: {
    key: 'fieldlens',
    title: 'Search Field Lens requests',
    // Only the most common filters are exposed here — search_field_lens_table
    // supports ~12 parameters (boost owner/account/opportunity, boost date ranges,
    // etc.); a full-parity form would be unusable. Mention the rest in a normal
    // chat message and the model can still pass them through.
    description: 'Search the Field Lens request tracker by keyword, type, status, or owner.',
    fields: [
      { name: 'search_term', label: 'Keyword', type: 'text', placeholder: 'e.g. Commvault' },
      { name: 'type_filter', label: 'Type', type: 'select', options: ['Third-Party App Validation', 'Product Enhancement'] },
      { name: 'status', label: 'Status', type: 'select', options: ['Open', 'Evaluating', 'Delivered', 'Rejected', 'Draft'] },
      { name: 'owner_name', label: 'Owner (or "me")', type: 'text', placeholder: 'e.g. me' },
    ],
    isValid: () => true,
    buildAnswer(values) {
      const filters = []
      if (values.search_term?.trim()) filters.push(`keyword "${values.search_term.trim()}"`)
      if (values.type_filter) filters.push(`type "${values.type_filter}"`)
      if (values.status) filters.push(`status "${values.status}"`)
      if (values.owner_name?.trim()) filters.push(`owner "${values.owner_name.trim()}"`)
      const filterText = filters.length ? filters.join(', ') : 'no filters (return everything visible)'
      return `Search Field Lens requests with ${filterText}. Call search_field_lens_table with these criteria.`
    },
  },
}
